/**
 * The installment schedule of a school's plan, for schools that pay in
 * installments.
 *
 * Source: the route the school panel's Installment Details table uses —
 * `school/plans-installments/{planId}` — with `user_id` naming the school,
 * since an admin token is not the school. That route needs a plan id, so the
 * school's plans are read first and the plan picked is its active plan when
 * that one has installments, otherwise its newest plan that has any
 * (`installment_count > 0`). A school with no such plan gets no `plan`, and
 * the screen leaves the table out entirely.
 *
 * Verified against staging on 2 Oct 2026: school 1115 → plan 343 (5 paid),
 * school 1242 → plan 1230 (1 paid, 5 pending).
 */

import moment from "moment";
import { useEffect, useState } from "react";
import type { InstallmentRow } from "shared/components/schoolAnalytics";
import {
  getPlanHistory,
  getPlanInstallments,
} from "shared/services/schoolMembershipService";

/** Enough to reach the active plan; a school holds a couple of dozen at most. */
const PLAN_SCAN_SIZE = 50;

const fmtDate = (value?: string | null) => {
  const m = moment(value ?? undefined);
  return value && m.isValid() ? m.format("D MMM YYYY") : "—";
};

const fmtAmount = (value: any) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n).toLocaleString("en-US") : "—";
};

/** A pending installment whose due date has passed is shown as overdue. */
const statusOf = (item: any) => {
  switch (item?.status) {
    case "paid":
      return "Paid";
    case "in_review":
      return "In review";
    case "rejected":
      return "Rejected";
    default:
      return item?.due_date &&
        moment(item.due_date).isBefore(moment(), "day")
        ? "Overdue"
        : "Pending";
  }
};

/** Same rule as the plan tables: levels 1 and 2 are billed in rupees. */
const currencyOf = (plan: any) =>
  plan?.currency_type === "PKR" ||
  plan?.plan_level === 1 ||
  plan?.plan_level === 2
    ? "Rs."
    : "$";

const hasInstallments = (plan: any) => Number(plan?.installment_count) > 0;

function useSchoolInstallments(schoolId: number) {
  const [plan, setPlan] = useState<any>();
  const [rows, setRows] = useState<InstallmentRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    setPlan(undefined);
    setRows([]);

    if (!Number.isFinite(schoolId)) {
      setLoading(false);
      return undefined;
    }

    setLoading(true);

    getPlanHistory(schoolId, { page: 1, per_page: PLAN_SCAN_SIZE })
      .then(({ data }: any) => {
        if (cancelled) return undefined;

        const plans: any[] = data?.data?.plans?.data ?? [];
        // Newest first, so `find` lands on the latest plan with installments.
        const picked =
          plans.find((p) => Number(p?.status) === 1 && hasInstallments(p)) ??
          plans.find(hasInstallments);

        if (!picked) return undefined;

        setPlan(picked);
        return getPlanInstallments(picked.id, schoolId).then(
          ({ data: body }: any) => {
            if (cancelled) return;

            const items: any[] = body?.data?.installments ?? [];
            setRows(
              items.map((item, index) => ({
                id: item?.id ?? index,
                number: `${index + 1} / ${items.length}`,
                amount: fmtAmount(item?.amount),
                status: statusOf(item),
                due: fmtDate(item?.due_date),
                paidOn: fmtDate(item?.paid_at),
              }))
            );
          }
        );
      })
      .catch(() => undefined)
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  const paid = rows.filter((row) => row.status === "Paid").length;
  const overdue = rows.filter((row) => row.status === "Overdue").length;
  const meta = plan
    ? [
        plan?.name,
        rows.length ? `${paid} of ${rows.length} paid` : undefined,
        overdue ? `${overdue} overdue` : undefined,
      ]
        .filter(Boolean)
        .join(" · ")
    : undefined;

  return {
    /** The plan the schedule belongs to; undefined hides the table. */
    plan,
    rows,
    loading,
    meta,
    currency: currencyOf(plan),
  };
}

export default useSchoolInstallments;
